import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { EXPORTS } from '../../api/ads';
import { TABLE_MAX_HEIGHT } from '../../theme';
import { formatMoney, formatNumber, formatPercent } from '../../utils/format';
import { listUrl } from '../ads/AdFilters';
import { NameCell } from '../ads/cells';
import DownloadMenu from '../table/DownloadMenu';
import StickyTableContainer from '../table/StickyTableContainer';

/**
 * The ads with the most spend in the range, with a bar showing each one's spend against the top ad. A row opens
 * the ad on the Ads page.
 */
export default function TopAds({ ads, query, params }) {
  const navigate = useNavigate();
  const theme = useTheme();
  const top = Math.max(...ads.map((ad) => Number(ad.spend)), 0);

  return (
    <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
      <Stack direction="row" spacing={1.5} justifyContent="space-between" alignItems="flex-start"
             sx={{ px: 2.5, pt: 2.5, pb: 1.5 }}>
        <Box>
          <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 600 }}>
            Top {ads.length || 10} ads by spend
          </Typography>
          <Typography variant="body2" color="text.secondary">Open an ad to see it on the Ads page.</Typography>
        </Box>
        <DownloadMenu path={EXPORTS.topAds} params={params} disabled={!ads.length} label="Top ads" />
      </Stack>
      <StickyTableContainer sx={{ maxHeight: TABLE_MAX_HEIGHT }}>
        <Table stickyHeader size="small" sx={{ minWidth: 820 }} aria-label="Top ads by spend">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 40 }}>#</TableCell>
              <TableCell>Ad</TableCell>
              <TableCell sx={{ width: 260 }}>Spend</TableCell>
              <TableCell align="right">Impressions</TableCell>
              <TableCell align="right">Clicks</TableCell>
              <TableCell align="right">CTR</TableCell>
              <TableCell align="right">CPC</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {ads.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 5, borderBottom: 0 }}>
                  <Typography variant="body2" color="text.secondary">No ad delivered in this range.</Typography>
                </TableCell>
              </TableRow>
            ) : ads.map((ad, index) => (
              <TableRow key={`${ad.ad_account_id}-${ad.object_id}`} hover
                        sx={{ cursor: ad.id ? 'pointer' : 'default' }}
                        onClick={() => ad.id && navigate(listUrl('/ads', query, {
                          ad_account: ad.ad_account_id, campaign: ad.campaign_id, adset: ad.adset_id,
                          search: ad.object_id,
                        }))}>
                <TableCell sx={{ color: 'text.secondary' }}>{index + 1}</TableCell>
                <TableCell>
                  <NameCell name={ad.name} secondary={ad.campaign_name && `${ad.campaign_name} › ${ad.adset_name}`} />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ flex: 1, height: 8, borderRadius: 4,
                      bgcolor: alpha(theme.palette.primary.light, 0.12) }}>
                      <Box sx={{ height: '100%', borderRadius: 4, bgcolor: 'primary.light',
                        width: `${top ? (Number(ad.spend) / top) * 100 : 0}%` }} />
                    </Box>
                    <Typography variant="body2" sx={{ minWidth: 84, textAlign: 'right', fontWeight: 500 }}>
                      {formatMoney(ad.spend, ad.currency)}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell align="right">{formatNumber(ad.impressions)}</TableCell>
                <TableCell align="right">{formatNumber(ad.clicks)}</TableCell>
                <TableCell align="right">{formatPercent(ad.ctr)}</TableCell>
                <TableCell align="right">{formatMoney(ad.cpc, ad.currency)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </StickyTableContainer>
    </Paper>
  );
}
